import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Navbar from "@/components/Navbar";
import NotifPrefs from "@/components/NotifPrefs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { UserCircle, Lock, Save } from "lucide-react";

const STATES = [
  "Andhra Pradesh","Arunachal Pradesh","Assam","Bihar","Chhattisgarh","Goa","Gujarat","Haryana",
  "Himachal Pradesh","Jharkhand","Karnataka","Kerala","Madhya Pradesh","Maharashtra","Manipur",
  "Meghalaya","Mizoram","Nagaland","Odisha","Punjab","Rajasthan","Sikkim","Tamil Nadu","Telangana",
  "Tripura","Uttar Pradesh","Uttarakhand","West Bengal","Andaman & Nicobar Islands","Chandigarh",
  "Dadra & Nagar Haveli and Daman & Diu","Delhi","Jammu & Kashmir","Ladakh","Lakshadweep",
  "Puducherry","Other",
];

export default function Profile() {
  const { user, refresh } = useAuth();
  const [form, setForm] = useState({ name: "", phone: "", address: "", state: "" });
  const [savingProfile, setSavingProfile] = useState(false);
  const [pw, setPw] = useState({ current_password: "", new_password: "", confirm: "" });
  const [savingPw, setSavingPw] = useState(false);
  const [pwErr, setPwErr] = useState("");

  useEffect(() => {
    if (user) {
      setForm({
        name: user.name || "",
        phone: user.phone || "",
        address: user.address || "",
        state: user.state || "",
      });
    }
  }, [user]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));
  const setPwField = (k) => (e) => setPw((p) => ({ ...p, [k]: e.target.value }));

  const saveProfile = async (e) => {
    e.preventDefault();
    // Only send fields the user filled in (skip empty strings) so a
    // hydration race on any Select doesn't cause a 422 from the backend.
    const payload = Object.fromEntries(
      Object.entries(form).filter(([, v]) => typeof v === "string" && v.trim().length > 0)
    );
    if (Object.keys(payload).length === 0) {
      toast.error("Fill at least one field to save");
      return;
    }
    setSavingProfile(true);
    try {
      await api.patch("/auth/me", payload);
      toast.success("Profile updated");
      refresh();
    } catch (e2) {
      toast.error(formatApiErrorDetail(e2.response?.data?.detail) || "Update failed");
    } finally { setSavingProfile(false); }
  };

  const changePassword = async (e) => {
    e.preventDefault();
    setPwErr("");
    if (pw.new_password !== pw.confirm) {
      setPwErr("New password and confirmation don't match");
      return;
    }
    setSavingPw(true);
    try {
      await api.post("/auth/change-password", {
        current_password: pw.current_password,
        new_password: pw.new_password,
      });
      toast.success("Password changed");
      setPw({ current_password: "", new_password: "", confirm: "" });
    } catch (e2) {
      setPwErr(formatApiErrorDetail(e2.response?.data?.detail) || "Change failed");
    } finally { setSavingPw(false); }
  };

  if (!user) return null;

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Navbar />
      <main className="max-w-4xl mx-auto px-6 py-12">
        <div className="mb-10">
          <div className="text-xs uppercase tracking-[0.3em] font-bold text-brand-gold mb-4">Profile</div>
          <h1 className="text-4xl lg:text-5xl font-black tracking-tight flex items-center gap-4" data-testid="profile-title">
            <UserCircle className="w-10 h-10" /> Account Settings
          </h1>
          <p className="mt-3 font-mono text-muted-foreground max-w-2xl">
            Apna contact info update karo ya password change karo. Email badalna ho to naya account banao.
          </p>
        </div>

        {/* Editable profile */}
        <form onSubmit={saveProfile} className="border-2 border-border p-6 space-y-5 mb-10" data-testid="profile-form">
          <div className="text-xs uppercase tracking-[0.3em] font-bold text-muted-foreground">Basic details</div>

          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs uppercase tracking-[0.2em] font-bold">Full name</Label>
              <Input required maxLength={100} value={form.name} onChange={set("name")}
                     className="mt-2 rounded-lg border-border h-11 font-mono" data-testid="profile-name-input" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-[0.2em] font-bold">Phone</Label>
              <Input required minLength={7} maxLength={20} value={form.phone} onChange={set("phone")}
                     className="mt-2 rounded-lg border-border h-11 font-mono" data-testid="profile-phone-input" />
            </div>
          </div>

          <div>
            <Label className="text-xs uppercase tracking-[0.2em] font-bold">Email</Label>
            <Input disabled value={user.email} className="mt-2 rounded-lg border-border h-11 font-mono bg-secondary/60 cursor-not-allowed" data-testid="profile-email-readonly" />
          </div>

          <div>
            <Label className="text-xs uppercase tracking-[0.2em] font-bold">Address</Label>
            <Textarea required maxLength={300} rows={2} value={form.address} onChange={set("address")}
                      className="mt-2 rounded-lg border-border font-mono resize-none" data-testid="profile-address-input" />
          </div>

          <div>
            <Label className="text-xs uppercase tracking-[0.2em] font-bold">State</Label>
            <Select value={form.state} onValueChange={set("state")} key={`state-${user?.id || "loading"}`}>
              <SelectTrigger className="mt-2 rounded-lg border-border h-11 font-mono" data-testid="profile-state-input">
                <SelectValue placeholder="Select state" />
              </SelectTrigger>
              <SelectContent className="max-h-64">
                {STATES.map((s) => (
                  <SelectItem key={s} value={s}>{s}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <Button type="submit" disabled={savingProfile} className="rounded-lg h-11 bg-brand-gold hover:bg-brand-gold/90 text-white text-xs uppercase tracking-[0.2em] font-bold" data-testid="profile-save-btn">
            <Save className="w-4 h-4 mr-2" /> {savingProfile ? "Saving…" : "Save changes"}
          </Button>
        </form>

        {/* Notification preferences */}
        <NotifPrefs />

        {/* Change password */}
        <form onSubmit={changePassword} className="border-2 border-border p-6 space-y-5" data-testid="password-form">
          <div className="flex items-center gap-2">
            <Lock className="w-4 h-4" />
            <div className="text-xs uppercase tracking-[0.3em] font-bold">Change password</div>
          </div>

          <div>
            <Label className="text-xs uppercase tracking-[0.2em] font-bold">Current password</Label>
            <Input type="password" required value={pw.current_password} onChange={setPwField("current_password")}
                   className="mt-2 rounded-lg border-border h-11 font-mono" data-testid="password-current-input" />
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs uppercase tracking-[0.2em] font-bold">New password (min 8)</Label>
              <Input type="password" required minLength={8} maxLength={128} value={pw.new_password} onChange={setPwField("new_password")}
                     className="mt-2 rounded-lg border-border h-11 font-mono" data-testid="password-new-input" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-[0.2em] font-bold">Confirm new password</Label>
              <Input type="password" required minLength={8} maxLength={128} value={pw.confirm} onChange={setPwField("confirm")}
                     className="mt-2 rounded-lg border-border h-11 font-mono" data-testid="password-confirm-input" />
            </div>
          </div>

          {pwErr && (
            <div className="border-l-4 border-red-500 bg-red-50 px-4 py-2 text-sm font-mono text-red-700" data-testid="password-error">
              {pwErr}
            </div>
          )}

          <Button type="submit" disabled={savingPw} className="rounded-lg h-11 bg-brand-crimson hover:bg-red-700 text-white text-xs uppercase tracking-[0.2em] font-bold" data-testid="password-save-btn">
            <Lock className="w-4 h-4 mr-2" /> {savingPw ? "Updating…" : "Change password"}
          </Button>
        </form>
      </main>
    </div>
  );
}
